import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import DownloadIcon from '@mui/icons-material/Download'
import DeleteIcon from '@mui/icons-material/Delete'
import ImageIcon from '@mui/icons-material/Image'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { router } from '@inertiajs/react'
import { useReturnTo, appendReturnTo } from '@/contexts/ReturnToContext'
import { ImagePreviewDialog } from './ImagePreviewDialog'

interface AgentRef {
  id: number
  username: string
  slug: string
}

export interface AttachmentData {
  id: number
  filename: string
  content_type: string
  byte_size: number
  description: string | null
  uploaded_by: AgentRef
  download_url: string
  view_url?: string
  created_at: string
  updated_at: string
}

interface Props {
  attachment: AttachmentData
  boardSlug: string
  taskId: number
  deleteUrl?: string
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function isImage(contentType: string): boolean {
  return contentType.startsWith('image/')
}

function isPdf(contentType: string): boolean {
  return contentType === 'application/pdf'
}

function FileIcon({ contentType }: { contentType: string }) {
  if (isImage(contentType)) return <ImageIcon color="action" />
  if (isPdf(contentType)) return <PictureAsPdfIcon color="action" />
  return <InsertDriveFileIcon color="action" />
}

export function AttachmentItem({ attachment, boardSlug, taskId, deleteUrl }: Props) {
  const returnTo = useReturnTo()
  const [previewOpen, setPreviewOpen] = useState(false)

  const handleDelete = () => {
    if (confirm('Delete this attachment?')) {
      const url = deleteUrl || `/tasks/boards/${boardSlug}/tasks/${taskId}/attachments/${attachment.id}`
      router.delete(appendReturnTo(url, returnTo))
    }
  }

  const handleClick = () => {
    const viewUrl = attachment.view_url || attachment.download_url
    if (isImage(attachment.content_type)) {
      setPreviewOpen(true)
    } else if (isPdf(attachment.content_type)) {
      window.open(viewUrl, '_blank', 'noopener')
    } else {
      window.location.href = attachment.download_url
    }
  }

  const actionTitle = isImage(attachment.content_type)
    ? 'Preview'
    : isPdf(attachment.content_type)
      ? 'Open in new tab'
      : 'Download'

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <FileIcon contentType={attachment.content_type} />
        <Box
          sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
          onClick={handleClick}
        >
          <Typography variant="body2" fontWeight={600} noWrap sx={{ '&:hover': { textDecoration: 'underline' } }}>
            {attachment.filename}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatFileSize(attachment.byte_size)} &middot; {attachment.uploaded_by.username}
            {attachment.description && ` \u2014 ${attachment.description}`}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={handleClick}
          title={actionTitle}
        >
          {isImage(attachment.content_type) ? (
            <ImageIcon fontSize="small" />
          ) : isPdf(attachment.content_type) ? (
            <OpenInNewIcon fontSize="small" />
          ) : (
            <DownloadIcon fontSize="small" />
          )}
        </IconButton>
        <IconButton size="small" onClick={handleDelete} title="Delete">
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
      {isImage(attachment.content_type) && (
        <ImagePreviewDialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          imageUrl={attachment.view_url || attachment.download_url}
          filename={attachment.filename}
        />
      )}
    </>
  )
}
